import { exportJWK, exportPKCS8, exportSPKI, generateKeyPair, generateSecret, importJWK, importPKCS8, importSPKI, importX509, type JWK } from "jose";
import { DEFAULT_ENCRYPTION, isEncryption, isSymmetric, SECRET_BYTES, WRAP_BYTES } from "./algorithms";
import { bytesFromBase64Url, toBase64Url } from "./token";
import type { Half } from "./types";

export async function generateKey(alg: string, enc: string = DEFAULT_ENCRYPTION): Promise<string> {
  if (alg.startsWith("HS")) return toBase64Url(crypto.getRandomValues(new Uint8Array(SECRET_BYTES[alg] ?? 32)));
  // jose knows how long each of these has to be, and dir's length is the content encryption algorithm's rather than its own.
  if (isSymmetric(alg)) {
    return (await exportJWK(await generateSecret(alg === "dir" ? enc : alg, { extractable: true }))).k ?? "";
  }
  const { privateKey } = await generateKeyPair(alg, { extractable: true });
  return exportPKCS8(privateKey);
}

// Whatever is in the box: a PEM, a JWK or a set of them, or — for the algorithms with no second half — bytes or text.
export async function loadKey(secret: string, alg: string, half: Half, kid?: unknown): Promise<CryptoKey | Uint8Array> {
  const text = secret.trim();
  if (!text) throw new Error("Required");
  if (text.startsWith("-----BEGIN")) return loadPem(text, alg, half);
  if (text.startsWith("{")) return loadJwk(text, alg, half, kid);
  if (!isSymmetric(alg)) throw new Error(`${alg} takes a key, so this needs a PEM or a JWK rather than a phrase`);
  if (isEncryption(alg)) return loadBytes(text, alg);
  // Not the trimmed text: a space at either end is part of the secret, and changes every signature it makes.
  return new TextEncoder().encode(secret);
}

// An AES key is bytes of an exact length rather than a phrase of any, so the box is read as the base64url of them.
function loadBytes(text: string, alg: string): Uint8Array {
  let bytes: Uint8Array;
  try {
    bytes = bytesFromBase64Url(text);
  } catch {
    throw new Error(`${alg} takes bytes written as base64url, and this is not base64url`);
  }
  const wanted = WRAP_BYTES[alg];
  if (wanted && bytes.length !== wanted) throw new Error(`${alg} takes ${wanted} bytes, and this is ${bytes.length}`);
  return bytes;
}

async function loadPem(pem: string, alg: string, half: Half): Promise<CryptoKey> {
  if (pem.startsWith("-----BEGIN PRIVATE KEY")) {
    const key = await importPKCS8(pem, alg, { extractable: true });
    return half === "private" ? key : publicFromPrivate(key, alg);
  }
  const spki = pem.startsWith("-----BEGIN PUBLIC KEY");
  // Asked before the private-half guard below, so a PKCS#1 key is told what it is rather than called a public one.
  if (!spki && !pem.startsWith("-----BEGIN CERTIFICATE")) {
    throw new Error(
      "Only PKCS#8 keys, SPKI keys and X.509 certificates are read; openssl pkcs8 -topk8 converts PKCS#1",
    );
  }
  if (half === "private") throw new Error("That is a public key, and this needs the private half");
  return spki ? importSPKI(pem, alg, { extractable: true }) : importX509(pem, alg, { extractable: true });
}

async function loadJwk(text: string, alg: string, half: Half, kid: unknown): Promise<CryptoKey | Uint8Array> {
  let parsed: JWK & { keys?: unknown };
  try {
    parsed = JSON.parse(text) as JWK;
  } catch {
    return Promise.reject(new Error("That key is not JSON"));
  }
  // What a jwks_uri serves is a set of keys rather than one, and the header's kid is what says which of them to use.
  const jwk = parsed.keys === undefined ? parsed : keyFromSet(parsed.keys, kid);
  // A private JWK carries its public half, and a key imported to sign is not one the browser will verify with.
  const wanted = half === "public" && jwk.kty !== "oct" ? publicJwk(jwk) : jwk;
  return importJWK(wanted, alg, { extractable: true });
}

// Picked by kid the way a verifier picks, and a set of one is only taken on trust when the header names no kid at all.
function keyFromSet(keys: unknown, kid: unknown): JWK {
  if (!Array.isArray(keys) || keys.length === 0) throw new Error("That key set holds no keys");
  if (typeof kid !== "string") {
    if (keys.length === 1) return keys[0] as JWK;
    throw new Error(`The token names no kid, and this set holds ${keys.length} keys to choose between`);
  }
  const named = (keys as JWK[]).filter((key) => key?.kid === kid);
  if (named.length === 0) throw new Error(`No key in this set has the kid "${kid}" the token asks for`);
  if (named.length > 1) throw new Error(`This set holds ${named.length} keys with the kid "${kid}"`);
  return named[0];
}

export async function publicFromPrivate(key: CryptoKey, alg: string): Promise<CryptoKey> {
  return await importJWK(publicJwk(await exportJWK(key)), alg, { extractable: true }) as CryptoKey;
}

// The private halves, and the flags that would carry "may sign" onto a key that is about to verify.
const PRIVATE_JWK_FIELDS = ["d", "p", "q", "dp", "dq", "qi", "oth", "key_ops", "use", "ext"];

export function publicJwk(jwk: JWK): JWK {
  const copy: Record<string, unknown> = { ...jwk };
  for (const field of PRIVATE_JWK_FIELDS) delete copy[field];
  return copy as JWK;
}

export async function publicKeyPem(key: CryptoKey | Uint8Array, alg: string): Promise<string> {
  if (key instanceof Uint8Array) return "";
  if (key.type === "public") return exportSPKI(key);
  if (key.type !== "private") return "";
  return exportSPKI(await publicFromPrivate(key, alg));
}
